import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import LoadingWrapper from '@afosto/components-react/LoadingWrapper';
import useIntl from '@afosto/utils-react/intl/useIntl';
import useForm from '@afosto/utils-react/validation/useForm';
import Box from '@material-ui/core/Box';
import Checkbox from '../Checkbox';
import Form from '../Form';
import ProductInventoryTable from '../ProductInventoryTable';
import ProductSearch from '../ProductSearch';
import TextField from '../TextField';
import translations from './translations';
import validationSchema, { noQuantityValidationSchema } from './validations';

const StackItemForm = ({
  addAnotherProduct,
  allowNegativeQuantity,
  onSubmit,
  onCancel,
  product,
  renderActions,
  showAddAnotherProduct,
  showProductInventory,
  showQuantity,
}) => {
  const intl = useIntl();
  const productRef = useRef(null);
  const quantityRef = useRef(null);

  const form = useForm({
    validationSchema: showQuantity ? validationSchema : noQuantityValidationSchema,
    clearOnSubmit: true,
    defaultValues: {
      product: product ?? null,
      sku: product?.sku ?? '',
      ean: product?.ean ?? '',
      description: product?.description ?? '',
      quantity: 1,
      addAnotherProduct: addAnotherProduct ?? true,
    },
    onSubmit: async data => {
      try {
        if (onSubmit) {
          await onSubmit(data);

          if (data.addAnotherProduct) {
            productRef.current.focus();
          }
        }

        return null;
      } catch (error) {
        return Promise.reject(error);
      }
    },
    onCancel,
  });
  const { setValue, watch } = form;
  const skuValue = watch('sku');

  /**
   * Handle selecting a product from the search suggestions.
   * @param {Event} ev
   * @param {object} newValue
   */
  const handleSelectProduct = (ev, newValue) => {
    setValue('product', newValue);

    if (newValue) {
      setValue('sku', newValue.sku);
      setValue('ean', newValue.ean);
      setValue('description', newValue.name);

      if (showQuantity) {
        quantityRef.current.focus();
      }
    } else {
      setValue('sku', '');
      setValue('ean', '');
      setValue('description', '');
    }
  };

  return (
    <LoadingWrapper isLoading={form.isSubmitting}>
      <Form form={form} renderActions={renderActions}>
        <ProductSearch
          name="product"
          label={intl.formatMessage(translations.fields.product.label)}
          onChange={handleSelectProduct}
          inputRef={productRef}
          autoFocus={!product?.sku}
        />

        <TextField name="sku" label={intl.formatMessage(translations.fields.sku.label)} />

        <TextField name="ean" label={intl.formatMessage(translations.fields.ean.label)} readOnly />

        <TextField
          name="description"
          label={intl.formatMessage(translations.fields.description.label)}
        />

        {showQuantity && (
          <TextField
            name="quantity"
            label={intl.formatMessage(translations.fields.quantity.label)}
            type="number"
            autoFocus={!!product?.sku}
            inputProps={
              !allowNegativeQuantity
                ? {
                    min: 1,
                  }
                : {}
            }
            inputRef={quantityRef}
          />
        )}

        {showProductInventory && (
          <Box mt={1} mb={3}>
            <ProductInventoryTable skus={skuValue ? [skuValue] : []} />
          </Box>
        )}

        {showAddAnotherProduct && (
          <Checkbox
            name="addAnotherProduct"
            label={intl.formatMessage(translations.fields.addAnotherProduct.label)}
          />
        )}
      </Form>
    </LoadingWrapper>
  );
};

StackItemForm.propTypes = {
  addAnotherProduct: PropTypes.bool,
  allowNegativeQuantity: PropTypes.bool,
  onSubmit: PropTypes.func,
  onCancel: PropTypes.func,
  product: PropTypes.object,
  renderActions: PropTypes.bool,
  showAddAnotherProduct: PropTypes.bool,
  showProductInventory: PropTypes.bool,
  showQuantity: PropTypes.bool,
};

StackItemForm.defaultProps = {
  addAnotherProduct: undefined,
  allowNegativeQuantity: false,
  onSubmit: undefined,
  onCancel: undefined,
  product: undefined,
  renderActions: undefined,
  showAddAnotherProduct: true,
  showProductInventory: false,
  showQuantity: true,
};

export default StackItemForm;
